import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import SocialProof from '../components/SocialProof';
import { useShopifyProducts } from '../hooks/useShopifyProducts';
import { handleImageError } from '../utils/imageOptimization';

// Marquee images - 8 editorial product/lifestyle shots
const MARQUEE_IMAGES = [
  { src: "https://customer-assets.emergentagent.com/job_1fca014f-ddd8-451a-ad68-17c2161f13c0/artifacts/s4eps1az_Handbag%20Editorial%201.png", alt: "The Bag burgundy" },
  { src: "https://customer-assets.emergentagent.com/job_1fca014f-ddd8-451a-ad68-17c2161f13c0/artifacts/024qqzr1_5f769d77-9a75-4761-87b1-289c0e63f12d.jpg", alt: "Layer necklace on body" },
  { src: "https://customer-assets.emergentagent.com/job_1fca014f-ddd8-451a-ad68-17c2161f13c0/artifacts/wjfu53dd_Instagram%20post%20-%2076.png", alt: "Cuff set on sand" },
  { src: "https://customer-assets.emergentagent.com/job_1fca014f-ddd8-451a-ad68-17c2161f13c0/artifacts/jky5geaz_b87a0697-237d-473b-92ea-96658d12fe27.webp", alt: "Gold ring cream fabric" },
  { src: "https://customer-assets.emergentagent.com/job_1fca014f-ddd8-451a-ad68-17c2161f13c0/artifacts/5hshf3ej_Untitled_Jewelry_Lookbook_Aesthetic_2026-03-11_20-31.png", alt: "Signet ring" },
  { src: "https://customer-assets.emergentagent.com/job_1fca014f-ddd8-451a-ad68-17c2161f13c0/artifacts/ebodoll4_Instagram%20post%20-%2074.png", alt: "Melt earrings" },
  { src: "https://customer-assets.emergentagent.com/job_1fca014f-ddd8-451a-ad68-17c2161f13c0/artifacts/b2t8oz3b_Instagram%20post%20-%2072.png", alt: "Drop ring on silk" },
  { src: "https://customer-assets.emergentagent.com/job_1fca014f-ddd8-451a-ad68-17c2161f13c0/artifacts/wxytlkvi_71142150-e4ca-4419-b48d-1b31ff48c76e.webp", alt: "Cuban chain" },
];

const Home = () => {
  // Fetch ALL product data directly from Shopify
  const { products: shopifyProducts } = useShopifyProducts();
  
  // CHANGE 5: Reorder products to put The Bag first
  const reorderedProducts = [...shopifyProducts].sort((a, b) => {
    if (a.name === 'THE BAG') return -1;
    if (b.name === 'THE BAG') return 1;
    return 0;
  });
  
  // Get first 6 products for featured section (with The Bag first)
  const featuredProducts = reorderedProducts.slice(0, 6);

  return (
    <div data-testid="home-page">
      {/* Hero Section - Full viewport IMAGE with handwritten overlay */}
      <section 
        className="relative overflow-hidden"
        style={{ height: '100vh' }}
        data-testid="hero-section"
      >
        {/* UPDATE 4: Hero Cover Image - loading="eager" for above-the-fold */}
        <img
          src="https://customer-assets.emergentagent.com/job_1fca014f-ddd8-451a-ad68-17c2161f13c0/artifacts/gf9ip9zp_homepage.png"
          alt="PLUMO Hero"
          width="2000"
          height="1200"
          className="absolute inset-0 w-full h-full"
          style={{
            objectFit: 'cover',
            objectPosition: 'center center',
          }}
          loading="eager"
          decoding="async"
          fetchpriority="high"
          onError={handleImageError}
        />

        {/* Subtle Gradient Overlay */}
        <div 
          className="absolute inset-0 z-[1]"
          style={{
            background: 'linear-gradient(to top, rgba(26,24,20,0.4) 0%, rgba(26,24,20,0.1) 40%, transparent 100%)',
          }}
        />

        {/* Handwritten Overlay Text */}
        <div 
          className="absolute z-[2]"
          style={{
            bottom: '12%',
            left: '48px',
          }}
        >
          <p 
            style={{
              fontFamily: "'Dawning of a New Day', cursive",
              fontSize: 'clamp(32px, 5vw, 56px)',
              color: '#F5F0E8',
              lineHeight: 1.2,
              marginBottom: '24px',
            }}
          >
            the things worth carrying.
          </p>
          {/* CHANGE 4: Hero CTA Button */}
          <Link
            to="/product/bag"
            style={{
              display: 'inline-block',
              backgroundColor: '#0D0D0B',
              color: '#FDFAF4',
              fontFamily: "'Space Mono', monospace",
              fontSize: '13px',
              fontWeight: 400,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              padding: '16px 32px',
              textDecoration: 'none',
              transition: 'opacity 200ms ease',
            }}
            className="hover:opacity-80"
            data-testid="hero-cta"
          >
            SHOP THE BAG →
          </Link>
        </div>

        {/* Scroll Indicator */}
        <div 
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-[2]"
        >
          <ChevronDown 
            className="w-5 h-5 animate-bounce"
            style={{ color: 'rgba(245,240,232,0.6)' }}
          />
        </div>
      </section>

      {/* Editorial Introduction - FIX 1, 2, 3 */}
      <section 
        className="bg-white"
        style={{ padding: '120px 24px' }}
        data-testid="intro-section"
      >
        <div className="max-w-[500px] mx-auto text-center">
          {/* UPDATE 1: Larger handwritten script headline */}
          <h2 
            className="font-handwritten"
            style={{
              fontSize: 'clamp(72px, 10vw, 120px)',
              color: '#1A1814',
              marginBottom: '24px',
              lineHeight: 1.0,
            }}
          >
            welcome to plumo
          </h2>
          {/* Subhead in Cormorant Garamond italic */}
          <p 
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontStyle: 'italic',
              fontSize: 'clamp(14px, 1.5vw, 16px)',
              color: 'rgba(26,24,20,0.7)',
              letterSpacing: '0.02em',
              lineHeight: 1.6,
            }}
          >
            jewelry, bags, and other things worth keeping close.
          </p>
          {/* Memorial Day savings in same style */}
          <p 
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontStyle: 'italic',
              fontSize: 'clamp(14px, 1.5vw, 16px)',
              color: 'rgba(26,24,20,0.7)',
              letterSpacing: '0.02em',
              marginTop: '16px',
            }}
          >
            memorial day savings — up to 60% off — through monday
          </p>
        </div>
      </section>

      {/* Product Grid - Clean, generous spacing */}
      <section 
        className="bg-white"
        style={{ padding: '80px 24px 120px' }}
        data-testid="featured-section"
      >
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
            {featuredProducts.map(product => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
          
          {/* View All Link - UPDATE 2: Inter, 14px, weight 500, underline */}
          <div className="text-center mt-16">
            <Link
              to="/shop"
              style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: '14px',
                fontWeight: 500,
                letterSpacing: '0.08em',
                color: '#0D0D0B',
                textDecoration: 'underline',
                textUnderlineOffset: '4px',
              }}
              className="hover:opacity-60 transition-opacity"
              data-testid="view-all-link"
            >
              browse the collection
            </Link>
          </div>
        </div>
      </section>

      {/* Editorial Image Block - Single full-bleed image */}
      <section className="w-full" data-testid="editorial-image-section">
        <div className="relative">
          <img
            src="https://customer-assets.emergentagent.com/job_editorial-jewelry/artifacts/hnsw67u6_Untitled_Surreal_Jewelry_Campaign_1_2026-03-11_16-21.png"
            alt="Editorial"
            width="1600"
            height="1000"
            className="w-full h-auto"
            style={{ maxHeight: '80vh', objectFit: 'cover' }}
            loading="lazy"
            decoding="async"
            onError={handleImageError}
          />
        </div>
        <div className="text-center py-8">
          <p 
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontWeight: 500,
              fontStyle: 'italic',
              fontSize: '20px',
              color: '#1A1814',
            }}
          >
            worth carrying.
          </p>
        </div>
      </section>

      {/* FIX 4: Two-image editorial block on WHITE background */}
      <section 
        className="w-full"
        style={{ 
          backgroundColor: '#FFFFFF',
          padding: '80px 0',
        }}
        data-testid="editorial-duo-section"
      >
        <div className="max-w-[1400px] mx-auto px-6">
          <div 
            className="grid grid-cols-1 md:grid-cols-2"
            style={{ gap: '24px' }}
          >
            {/* Image A: Calla lily with gold ring */}
            <div className="aspect-[4/5] overflow-hidden">
              <img
                src="https://customer-assets.emergentagent.com/job_1fca014f-ddd8-451a-ad68-17c2161f13c0/artifacts/b2t8oz3b_Instagram%20post%20-%2072.png"
                alt="Gold ring on calla lily"
                width="600"
                height="750"
                className="w-full h-full object-cover"
                loading="lazy"
                decoding="async"
                onError={handleImageError}
              />
            </div>
            {/* Image B: Ear with gold earring and monstera */}
            <div className="aspect-[4/5] overflow-hidden">
              <img
                src="https://customer-assets.emergentagent.com/job_1fca014f-ddd8-451a-ad68-17c2161f13c0/artifacts/ebodoll4_Instagram%20post%20-%2074.png"
                alt="Gold earring with monstera leaf"
                width="600"
                height="750"
                className="w-full h-full object-cover"
                loading="lazy"
                decoding="async"
                onError={handleImageError}
              />
            </div>
          </div>
        </div>
      </section>

      {/* UPDATE 3: Scrolling Marquee Photo Strip */}
      <section 
        className="w-full overflow-hidden"
        style={{ 
          backgroundColor: '#F5F0E8',
          margin: '80px 0',
        }}
        data-testid="marquee-strip"
      >
        <div 
          className="flex animate-marquee-strip"
          style={{
            width: 'fit-content',
          }}
        >
          {/* First set of images */}
          {MARQUEE_IMAGES.map((img, index) => (
            <div 
              key={`first-${index}`}
              className="flex-shrink-0"
              style={{
                height: 'clamp(220px, 30vw, 320px)',
                marginRight: '16px',
              }}
            >
              <img
                src={img.src}
                alt={img.alt}
                width="240"
                height="320"
                className="h-full w-auto object-cover"
                loading="lazy"
                decoding="async"
                onError={handleImageError}
              />
            </div>
          ))}
          {/* Duplicate set for seamless loop */}
          {MARQUEE_IMAGES.map((img, index) => (
            <div 
              key={`second-${index}`}
              className="flex-shrink-0"
              style={{
                height: 'clamp(220px, 30vw, 320px)',
                marginRight: '16px',
              }}
            >
              <img
                src={img.src}
                alt={img.alt}
                width="240"
                height="320"
                className="h-full w-auto object-cover"
                loading="lazy"
                decoding="async"
                onError={handleImageError}
              />
            </div>
          ))}
        </div>
      </section>

      {/* Social Proof - Restyled */}
      <SocialProof />
    </div>
  );
};

export default Home;
